/**
 * O ícone de um Tipo de Tarefa, onde quer que ele apareça (28/09/2026).
 *
 * O `icone` do Tipo é texto curto e anulável no serviço (`TAMANHO_DO_ICONE`, 11.6) — um emoji,
 * na intenção original. O Gustavo pediu **o logotipo do WhatsApp** na pílula do Tipo WhatsApp, e
 * não o balão genérico: nenhum emoji o desenha, e um `<img>` por Tipo seria um segundo campo no
 * contrato. A saída é um **token reservado**: o Tipo configurado com `whatsapp` (cabe no teto do
 * serviço, que só limita o tamanho) é desenhado aqui como SVG inline, e qualquer outro texto
 * continua sendo mostrado como está.
 *
 * Todo lugar que imprimia `tipo.icone` cru passa por este componente — a tabela de Tarefas, as
 * pílulas do diálogo, as abas do compositor e a lista de Configuração → Tarefas — porque é
 * assim que o token vale igual nos quatro. Quem precisa de **texto** (uma `<option>`, que não
 * aceita markup) usa `rotuloDoIcone`.
 *
 * O ícone é decoração: o nome do Tipo está sempre ao lado, e `aria-hidden` mora em quem monta.
 */

import './icone-do-tipo.css'

/** O token que a paleta guarda no lugar de um emoji — e que o desenho abaixo reconhece. */
export const ICONE_DO_WHATSAPP = 'whatsapp'

/** O que uma `<option>` mostra para o token: ela não desenha SVG. */
export const ROTULO_DO_ICONE_DO_WHATSAPP = 'Logotipo do WhatsApp'

/**
 * O logotipo do WhatsApp (o balão com o fone), em traço único. É o desenho do Simple Icons, no
 * verde da régua (`--verde`), que é o verde que o SPA tem — o token não conhece o verde da marca.
 */
function LogotipoDoWhatsapp() {
  return (
    <svg
      className="icone-do-tipo__whatsapp"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
      />
    </svg>
  )
}

/**
 * O ícone do Tipo: o SVG para o token, o texto (emoji) para o resto, `null` sem ícone. Quem chama
 * decide a moldura (a caixinha da tabela, a pílula do diálogo) e o `aria-hidden`.
 */
export function IconeDoTipo({ icone }: { icone: string | null | undefined }) {
  if (icone === null || icone === undefined || icone === '') {
    return null
  }
  if (icone === ICONE_DO_WHATSAPP) {
    return <LogotipoDoWhatsapp />
  }
  return <>{icone}</>
}

/** O texto de um ícone onde só cabe texto (`<option>`): o rótulo do token, ou o próprio emoji. */
export function rotuloDoIcone(icone: string | null | undefined): string {
  if (icone === null || icone === undefined || icone === '') {
    return ''
  }
  return icone === ICONE_DO_WHATSAPP ? ROTULO_DO_ICONE_DO_WHATSAPP : icone
}

/**
 * "Ícone + nome" em texto, para as `<option>` que listam Tipos. Com o token, só o nome: escrever
 * "Logotipo do WhatsApp WhatsApp" numa opção seria pior do que não ter ícone.
 */
export function nomeComIcone(tipo: { nome: string; icone: string | null | undefined }): string {
  if (tipo.icone === null || tipo.icone === undefined || tipo.icone === '') {
    return tipo.nome
  }
  return tipo.icone === ICONE_DO_WHATSAPP ? tipo.nome : `${tipo.icone} ${tipo.nome}`
}
